import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import Footer from './footer'
import GuestHydrationRunner from './GuestHydrationRunner'
import GuestInvoiceSection from './GuestInvoiceSection'
import GuestSignInOverlay from './GuestSignInOverlay'
import Hero from './hero'
import HowItWorks from './how-it-works'
import LandingCta from './cta'
import LandingFaq from './faq'
import { useAuth } from '../../context/auth'
import {
  clearGuestPendingIntent,
  GuestPendingIntent,
  hasGuestPendingIntent,
} from '../../lib/guestInvoiceDraftStorage'
import { AUTH_REDIRECT_PATH_KEY } from '../../lib/signIn'

const LandingPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [guestSignInIntent, setGuestSignInIntent] = React.useState<GuestPendingIntent | null>(null)
  const [guestSignInDismissNonce, setGuestSignInDismissNonce] = React.useState(0)
  const [guestHydrationActive, setGuestHydrationActive] = React.useState(false)
  const shouldRunGuestHydration = !!user && (hasGuestPendingIntent() || guestHydrationActive)

  const handleGuestSignInRequest = React.useCallback((intent: GuestPendingIntent) => {
    setGuestSignInIntent(intent)
  }, [])

  const handleCloseGuestSignIn = React.useCallback(() => {
    clearGuestPendingIntent()
    setGuestSignInIntent(null)
    setGuestSignInDismissNonce((n) => n + 1)
  }, [])

  React.useEffect(() => {
    if (user && hasGuestPendingIntent()) {
      setGuestHydrationActive(true)
    }
  }, [user])

  React.useEffect(() => {
    if (!user) return
    if (location.pathname !== '/') return
    if (hasGuestPendingIntent() || guestHydrationActive) return

    const defaultPath = user?.businessDetails?.name ? '/dashboard' : '/invoices/new'
    const redirectPath = localStorage.getItem(AUTH_REDIRECT_PATH_KEY) ?? defaultPath
    localStorage.removeItem(AUTH_REDIRECT_PATH_KEY)
    navigate(redirectPath, { replace: true })
  }, [user, location.pathname, navigate, guestHydrationActive])

  return (
    <>
      <div className='bg-light'>
        <Hero />
        <div className='py-sm-4' />
      </div>
      <div className='position-relative'>
        <div className='bg-light position-absolute top-0 w-100' style={{ height: '150px' }}></div>
        {shouldRunGuestHydration ? (
          <GuestHydrationRunner />
        ) : !user && (
          <>
            <div className='mt-n5'>
              <GuestInvoiceSection
                onGuestSignInRequest={handleGuestSignInRequest}
                guestSignInDismissNonce={guestSignInDismissNonce}
              />
            </div>
            <GuestSignInOverlay
              open={guestSignInIntent != null}
              intent={guestSignInIntent}
              onClose={handleCloseGuestSignIn}
            />
          </>
        )}
      </div>
      <div className='bg-light py-5'>
        <HowItWorks />
      </div>
      <LandingFaq />
      <LandingCta />
      <Footer />
    </>
  )
}

export default LandingPage
